// AddEditSubjectModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalChipSelector } from '../../../components/GlobalComponents/GlobalChipSelector';

export type SubjectType = 'regular' | 'optional' | 'combined';
export type SubjectStatus = 'active' | 'inactive';

export interface SubjectRow {
  subject_id: string;
  subject_name: string;
  class_id: string;
  subject_type: SubjectType;
  main_subject_id: string | null;
  status: SubjectStatus;
}

interface AddEditSubjectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  classId: string;
  className: string;
  sessionId: string | null;
  // Subject being edited, or null when creating a new one.
  editingSubject: SubjectRow | null;
  // All subjects currently loaded for this class — used for the
  // "combined" main-subject picker and for a client-side duplicate-name check.d
  existingSubjects: SubjectRow[];
}

const subjectTypeOptions = [
  { value: 'regular', label: 'Regular' },
  { value: 'optional', label: 'Optional' },
  { value: 'combined', label: 'Combined' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const AddEditSubjectModal: React.FC<AddEditSubjectModalProps> = ({
  open,
  onClose,
  onSuccess,
  classId,
  className,
  sessionId,
  editingSubject,
  existingSubjects,
}) => {
  const toast = useRef<Toast>(null);
  const isEdit = !!editingSubject;

  const [subjectName, setSubjectName] = useState('');
  const [subjectType, setSubjectType] = useState<SubjectType>('regular');
  const [mainSubjectId, setMainSubjectId] = useState<string>('');
  const [status, setStatus] = useState<SubjectStatus>('active');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingSubject) {
      setSubjectName(editingSubject.subject_name);
      setSubjectType(editingSubject.subject_type);
      setMainSubjectId(editingSubject.main_subject_id || '');
      setStatus(editingSubject.status);
    } else {
      setSubjectName('');
      setSubjectType('regular');
      setMainSubjectId('');
      setStatus('active');
    }
  }, [open, editingSubject]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  // Candidates for "main subject" — same class, not itself, not another
  // combined subject (a combined subject shouldn't point at another one).
  const mainSubjectOptions = existingSubjects
    .filter((s) => s.subject_type !== 'combined' && s.subject_id !== editingSubject?.subject_id)
    .map((s) => ({ value: s.subject_id, label: s.subject_name }));

  const handleSubmit = async () => {
    const trimmedName = subjectName.trim();

    if (!trimmedName) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Subject name is required', life: 4000 });
      return;
    }

    const duplicate = existingSubjects.some(
      (s) => s.subject_id !== editingSubject?.subject_id && s.subject_name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate) {
      toast.current?.show({ severity: 'error', summary: 'Duplicate Subject', detail: `"${trimmedName}" already exists for this class`, life: 4500 });
      return;
    }

    if (!isEdit && !sessionId) {
      toast.current?.show({ severity: 'error', summary: 'Session Error', detail: 'No active session found. Please reload and try again.', life: 5000 });
      return;
    }

    setLoading(true);
    try {
      if (isEdit && editingSubject) {
        const payload = {
          subject_name: trimmedName,
          subject_type: subjectType,
          status,
        };
        const res = await apiClient.put(`/subjects/${editingSubject.subject_id}`, payload);
        if (res.data.status !== 'success') throw new Error(res.data.message || 'Failed to update subject');
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Subject updated successfully!', life: 3500 });
      } else {
        const payload: Record<string, unknown> = {
          session_id: sessionId,
          class_id: classId,
          subject_name: trimmedName,
          subject_type: subjectType,
        };
        if (subjectType === 'combined' && mainSubjectId) {
          payload.main_subject_id = mainSubjectId;
        }
        const res = await apiClient.post('/subjects', payload);
        if (res.data.status !== 'success') throw new Error(res.data.message || 'Failed to add subject');
        toast.current?.show({ severity: 'success', summary: 'Added', detail: 'Subject added successfully!', life: 3500 });
      }

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 250);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || err.message || 'Something went wrong',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div
        style={{
          width: '54px',
          height: '54px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className={isEdit ? 'pi pi-pencil' : 'pi pi-plus-circle'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', marginBottom: '4px' }}>
          {isEdit ? 'Edit Subject' : 'Add Subject'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          <strong>{className}</strong>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.subject-modal .p-dialog');
      const mask = document.querySelector('.subject-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '520px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="subject-modal"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '11.5px',
                fontWeight: 700,
                color: '#1e40af',
                marginBottom: '8px',
                letterSpacing: '0.02em',
              }}
            >
              Subject Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="e.g. Mathematics"
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1.5px solid #e5e7eb',
                fontSize: '14px',
                fontWeight: 500,
                color: '#1f2937',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
              onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
            />
          </div>

          <GlobalChipSelector
            label="Subject Type"
            icon="pi pi-tags"
            value={subjectType}
            onChange={(v) => setSubjectType(v as SubjectType)}
            options={subjectTypeOptions}
            required
            disabled={loading}
          />

          {subjectType === 'combined' && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  color: '#1e40af',
                  marginBottom: '8px',
                  letterSpacing: '0.02em',
                }}
              >
                Main Subject
              </label>
              <select
                value={mainSubjectId}
                onChange={(e) => setMainSubjectId(e.target.value)}
                disabled={loading || mainSubjectOptions.length === 0}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1f2937',
                  outline: 'none',
                  background: 'white',
                }}
              >
                <option value="">
                  {mainSubjectOptions.length === 0 ? 'No other subjects yet' : 'Select main subject'}
                </option>
                {mainSubjectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isEdit && (
            <GlobalChipSelector
              label="Status"
              icon="pi pi-toggle-on"
              value={status}
              onChange={(v) => setStatus(v as SubjectStatus)}
              options={statusOptions}
              required
              disabled={loading}
            />
          )}

          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <GlobalButton
              label={loading ? (isEdit ? 'Saving...' : 'Adding...') : isEdit ? 'Save Changes' : 'Add Subject'}
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddEditSubjectModal;
